import React, { useCallback, useState } from "react";
import type { GetStaticProps } from "next";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import copy from "clipboard-copy";

import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "next-i18next";
import clsx from "clsx";
import { toaster } from "evergreen-ui";

type Props = {
  // Add custom props here
};
function randomPassword(params: {
  length: number;
  upperCase: boolean;
  numberCase: boolean;
  fuhaoCase: boolean;
}) {
  const {
    length = 6,
    upperCase = false,
    numberCase = false,
    fuhaoCase = false
  } = params;
  let chars = "abcdefghijklmnopqrstuvwxyz";
  if (upperCase) {
    chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  }
  if (numberCase) {
    chars += "0123456789";
  }
  if (fuhaoCase) {
    chars += "!@#$%^&*()-_";
  }
  let password = "";
  for (var i = 0; i <= length; i++) {
    const randomNumber = Math.floor(Math.random() * chars.length);
    password += chars.substring(randomNumber, randomNumber + 1);
  }
  return password;
}
export default function RandomPasswordGenerator() {
  const { t } = useTranslation("password");

  const [params, setParams] = useState<{
    length: number;
    upperCase: boolean;
    numberCase: boolean;
    fuhaoCase: boolean;
  }>({
    length: 12,
    upperCase: true,
    numberCase: true,
    fuhaoCase: false
  });

  const [result, setResult] = useState<string>(randomPassword(params));

  const onChange = (type: string, value: number | boolean) => {
    const newParams = {
      ...params,
      [type]: value
    };
    setParams(newParams);
    setResult(randomPassword(newParams));
  };

  return (
    <Card className={clsx("w-full mt-8 md:mt-0")}>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
        <CardDescription>{t("subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex items-center space-x-4 rounded-md border p-4">
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium leading-none">
              {t("include.number")}
            </p>
            <p className="text-sm text-muted-foreground">0123456789</p>
          </div>
          <Switch
            checked={params.numberCase}
            onCheckedChange={value => onChange("numberCase", value)}
          />
        </div>
        <div className=" flex items-center space-x-4 rounded-md border p-4">
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium leading-none">
              {t("include.upper-letters")}
            </p>
            <p className="text-sm text-muted-foreground">
              ABCDEFGHIJKLMNOPQRSTUVWXYZ
            </p>
          </div>
          <Switch
            checked={params.upperCase}
            onCheckedChange={value => onChange("upperCase", value)}
          />
        </div>
        <div className=" flex items-center space-x-4 rounded-md border p-4">
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium leading-none">
              {t("include.special-characters")}
            </p>
            <p className="text-sm text-muted-foreground">{"!@#$%^&*()-_"}</p>
          </div>
          <Switch
            checked={params.fuhaoCase}
            onCheckedChange={value => onChange("fuhaoCase", value)}
          />
        </div>
        <div className=" flex items-center space-x-4 rounded-md border p-4">
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium leading-none">
              {t("include.length")}
            </p>
            <p className="text-sm text-muted-foreground">
              {`${t("include.length-message")} ${params.length}`}
            </p>
          </div>
          <div className="w-[400px]">
            <Slider
              defaultValue={[params.length]}
              max={30}
              step={1}
              min={4}
              onValueChange={value => onChange("length", value[0])}
            />
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex-col space-y-4">
        <div className="w-full">
          <Button
            className="w-[180px]"
            size="sm"
            onClick={() => {
              setResult(randomPassword(params));
            }}
          >
            {t("action.generator")}
          </Button>
        </div>
        <div className="w-full">
          <Textarea
            readOnly
            placeholder={t("tooltip.textarea")}
            value={result}
          />
        </div>
        <div className="w-full flex items-center space-x-1">
          <Button
            className="w-[120px]"
            size="sm"
            onClick={() => {
              copy(result);
              toaster.success(t("tooltip.copy-message"));
            }}
          >
            {t("action.copy")}
          </Button>
          <Button
            className="w-[120px]"
            variant="ghost"
            size="sm"
            onClick={() => {
              setResult("");
            }}
          >
            {t("action.clear")}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? "en", [
      "common",
      "description",
      "title",
      "keywords",
      "password"
    ]))
  }
});
